import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import paises from 'src/app/common/country';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { EntidadesApiService } from '../../../../../service/api/entidades-api.service';
import { EntidadModel } from 'src/app/models/reto/entidad.model';
import { ProvinciasInterface } from 'src/app/models/reto/provincia.model';
import { FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import {
    EntidadesDropdownOption,
    EntidadesFormDefinition,
} from './form-control/form-definition';

@Component({
    selector: 'app-entidades-form',
    standalone: false,
    templateUrl: './entidades-form.html',
    styleUrls: ['./entidades-form.scss'],
})
export class EntidadesFormComponent extends BaseForm<EntidadModel> implements OnInit {
    private static readonly SPAIN_COUNTRY_CODE = 'ES';

    public form!: EntidadesFormDefinition;


    public error = '';

    private provinceOptions: EntidadesDropdownOption[] = [];

    public constructor(
        public override router: Router,
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public entidadesApi: EntidadesApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            entidadesApi.EntidadesPUT,
            entidadesApi.EntidadesPOST,
            'entidades',
            'Editar entidad',
            'Crear entidad',
            'Duplicar entidad',
        );
    }// constructor();

    /**
     * Initializes the shared form definition and starts progressive dropdown loading.
     *
     * @public
     *
     * @returns Nothing.
     */
    public ngOnInit(): void {
        this.dropdownsFinalized = false;
        this.form = new EntidadesFormDefinition(this.getType(), this.getInitialModel());
        this.model = this.form.model as EntidadModel;

        void this.initFormData();
        this.softInit(this.getType());
    }// ngOnInit();

    /**
     * Synchronizes reactive form changes with the shared form definition.
     *
     * @public
     *
     * @param formModel - Current reactive form value.
     * @param lastChangedFieldName - Last field changed by the user.
     *
     * @returns Nothing.
     */
    public formChanges(
        formModel: ReactiveFormValue<EntidadModel>,
        lastChangedFieldName: ReactiveFormChangedField<EntidadModel>,
    ): void {
        this.form.model = { ...this.form.model, ...formModel } as ReactiveFormValue<EntidadModel>;
        this.form.lastChangedFieldName = lastChangedFieldName;
        this.model = { ...this.model, ...formModel } as EntidadModel;

        if (lastChangedFieldName === 'pais') {
            this.syncProvinceOptions();
        }// if();
    }// formChanges();

    /**
     * Resets pending requests and returns to the entity list.
     *
     * @public
     *
     * @returns Nothing.
     */
    public override goBack(): void {
        this.request.reset();
        this.router.navigate(['dashboard', 'reto-entidades']);
    }// goBack();

    /**
     * Returns the initial model used before the backend record is loaded.
     *
     * @private
     *
     * @returns Initial entity model values.
     */
    private getInitialModel(): Partial<EntidadModel> {
        if (this.getType() === FormType.CREATION) {
            return { pais: EntidadesFormComponent.SPAIN_COUNTRY_CODE };
        }// if();

        return this.getModel() as Partial<EntidadModel>;
    }// getInitialModel();

    /**
     * Loads dropdown data and notifies the reactive form builder when ready.
     *
     * @private
     *
     * @returns A promise resolved when dropdown initialization finishes.
     */
    private async initFormData(): Promise<void> {
        try {
            this.loadCountries();
            this.provinceOptions = this.buildProvinceOptions(await this.entidadesApi.provinciasGET.toPromise());
            this.syncProvinceOptions();
        } catch (error) {
            this.error = 'No se han podido cargar los datos auxiliares de entidades.';
            console.error(this.error, error);
        } finally {
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();
        }
    }// initFormData();

    /**
     * Loads country options into the shared form definition.
     *
     * @private
     *
     * @returns Nothing.
     */
    private loadCountries(): void {
        const countries: EntidadesDropdownOption[] = [
            { label: '...', value: null },
            ...paises
                .filter((country) => country.value !== '')
                .map((country) => ({
                    label: country.label ?? '',
                    value: country.value ?? null,
                })),
        ];

        this.form.setCountryOptions(countries);
    }// loadCountries();

    /**
     * Converts API provinces into dropdown options.
     *
     * @private
     *
     * @param provinces - Province records returned by the API.
     *
     * @returns Dropdown options used by the reactive form builder.
     */
    private buildProvinceOptions(provinces: ProvinciasInterface[]): EntidadesDropdownOption[] {
        return [
            { label: '...', value: null },
            ...provinces.map((province) => ({
                label: province.nombre ?? '',
                value: province.id?.toString() ?? null,
            })),
        ];
    }// buildProvinceOptions();

    /**
     * Shows Spanish provinces when Spain is selected or while edit data is still loading.
     *
     * @private
     *
     * @returns Nothing.
     */
    private syncProvinceOptions(): void {
        const selectedCountry = String(this.form.model.pais ?? '');

        if (selectedCountry === '' || selectedCountry === EntidadesFormComponent.SPAIN_COUNTRY_CODE) {
            this.form.setProvinceOptions(this.provinceOptions);
        } else {
            this.form.resetProvinceOptions();
        }// if();

        this.dropdownLoadTrigger.next();
    }// syncProvinceOptions();
}// class;

